/**
 * One element made droppable — Pragmatic's `dropTargetForElements()`, bound to React's lifecycle,
 * and the closest-edge hitbox when the target is an insertion point between siblings.
 *
 * ── A refused target is not entered, and that IS the refusal ─────────────────────────────────
 * View ADR §11: "A refused target renders as refused *during* the drag — no indicator, no cursor
 * change — rather than accepting the drop and erroring afterwards." `canDrop` returning `false` is
 * exactly that: the adapter never makes this element the drop target, so `isOver` stays false, no
 * indicator paints, and a release over it lands nowhere.
 *
 * The REASON therefore does not reach the trainer from a pointer hover, and that is deliberate. A
 * live region firing "ta faza jest zamknięta" every time the pointer crossed a locked column would
 * be a torrent nobody can read mid-gesture. The reason lives where the trainer explicitly aimed at
 * the target: the ⋯ menu's entry, which stays focusable and states it (view ADR §12), and the
 * keyboard path, which announces it on the attempted drop.
 *
 * ── `canDrop` and `getData` are read from refs, for the same reason as `useDraggableItem` ────
 * Both are called REPEATEDLY while a drag is over the element, and both close over the store's
 * current answer. Making them effect dependencies would rebind the target on every render — which,
 * mid-gesture, drops the element out of the drag and back into it, and the edge flickers.
 */

import { useEffect, useRef, useState, type RefObject } from 'react';

import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import {
  attachClosestEdge,
  extractClosestEdge,
  type Edge,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';

export interface DropTarget {
  /**
   * Which side of this element the pointer is nearest — only when `allowedEdges` was given, and
   * `null` whenever the element is not the current drop target.
   */
  readonly edge: Edge | null;
  readonly isOver: boolean;
}

export function useDropTarget(options: {
  /** The element that RECEIVES. Owned by the component — see `useDraggableItem`'s note on why. */
  readonly ref: RefObject<HTMLElement | null>;
  /** Attached to the target, so a monitor or a sibling can read what was dropped on. */
  readonly data: Record<string, unknown>;
  /** Asked repeatedly during a drag. `false` means the element is never entered — see the header. */
  readonly canDrop: (source: Record<string, unknown>) => boolean;
  /**
   * Turns the element into an INSERTION POINT: the hitbox reports which of these edges the pointer
   * is nearest, and the caller paints a line there. Omit for a whole-element target (a weekday slot,
   * which the canon fills rather than draws a line beside).
   */
  readonly allowedEdges?: readonly Edge[];
}): DropTarget {
  const [edge, setEdge] = useState<Edge | null>(null);
  const [isOver, setOver] = useState(false);
  const { ref, data, canDrop } = options;

  // Written in an EFFECT, never during render — see `useDraggableItem`. Both are read only from
  // callbacks the adapter invokes while a drag is in progress.
  const dataRef = useRef(data);
  const canDropRef = useRef(canDrop);
  useEffect(() => {
    dataRef.current = data;
    canDropRef.current = canDrop;
  }, [data, canDrop]);

  // A stable key for the edge list, so a caller writing `allowedEdges={['top','bottom']}` inline
  // does not rebind the target on every render.
  const edgeKey = options.allowedEdges?.join('|') ?? '';

  useEffect(() => {
    const element = ref.current;
    if (element === null) return;
    const allowedEdges = edgeKey === '' ? null : (edgeKey.split('|') as Edge[]);

    return dropTargetForElements({
      element,
      canDrop: ({ source }) => canDropRef.current(source.data),
      getData: ({ input }) =>
        allowedEdges === null
          ? dataRef.current
          : attachClosestEdge({ ...dataRef.current }, { element, input, allowedEdges }),
      onDrag: ({ self }) => {
        if (allowedEdges !== null) setEdge(extractClosestEdge(self.data));
      },
      onDragEnter: ({ self }) => {
        setOver(true);
        if (allowedEdges !== null) setEdge(extractClosestEdge(self.data));
      },
      onDragLeave: () => {
        setOver(false);
        setEdge(null);
      },
      onDrop: () => {
        setOver(false);
        setEdge(null);
      },
    });
  }, [ref, edgeKey]);

  return { edge, isOver };
}
